import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Lock, Check } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-stone-50 to-stone-100 px-4">
        <div className="zen-card p-8 max-w-md w-full text-center">
          <p className="text-warm-charcoal/60">Invalid or missing reset link.</p>
          <Button onClick={() => navigate('/login')} className="mt-4 bg-hunter-green text-white">Go to Login</Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-stone-50 to-stone-100 px-4">
        <div className="zen-card p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
            <Check className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Password Reset!</h2>
          <p className="text-sm text-warm-charcoal/60 mb-6">Your password has been updated. You can now log in.</p>
          <Button onClick={() => navigate('/login')} className="w-full bg-hunter-green text-white">Log In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-stone-50 to-stone-100 px-4">
      <div className="zen-card p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-hunter-green/10 mx-auto mb-3 flex items-center justify-center">
            <Lock className="w-6 h-6 text-hunter-green" />
          </div>
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>Set New Password</h2>
          <p className="text-xs text-warm-charcoal/50 mt-1">Choose a strong password for your account</p>
        </div>
        
        <form onSubmit={handleReset} className="space-y-4">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password (min 6 characters)"
            required
            className="h-11"
            data-testid="reset-new-password"
          />
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            required
            className="h-11"
            data-testid="reset-confirm-password"
          />
          <Button type="submit" disabled={loading} className="w-full h-11 bg-hunter-green text-white" data-testid="reset-submit">
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
